import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError } from '@/lib/api/client'
import { settingsApi } from '@/lib/api/settings'
import {
  applyTheme,
  persistTheme,
  readStoredTheme,
  resolveTheme,
  type ThemeMode,
} from '@/lib/theme'

interface ThemeContextValue {
  theme: ThemeMode
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => readStoredTheme())
  const [hydratedFromServer, setHydratedFromServer] = useState(false)

  const resolvedTheme = useMemo(() => resolveTheme(theme), [theme])

  useEffect(() => {
    applyTheme(theme)
    persistTheme(theme)
  }, [theme])

  useEffect(() => {
    if (hydratedFromServer) return

    settingsApi
      .getAppearance()
      .then((appearance) => {
        setThemeState(appearance.theme)
        setHydratedFromServer(true)
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) return
        setHydratedFromServer(true)
      })
  }, [hydratedFromServer])

  useEffect(() => {
    if (theme !== 'system') return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = useCallback((next: ThemeMode) => {
    setThemeState(next)
    void settingsApi.saveAppearance({ theme: next }).catch(() => {
      // localStorage fallback already applied
    })
  }, [])

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
    }),
    [theme, resolvedTheme, setTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
